import type { Session } from './api/auth'
import { canReach } from './nav'
import { navSubjectFrom } from './navSubject'
import { destinationByPrefix } from './navCurrent'
import { destinationForPath } from './navViews'

/**
 * Where sign-in sends her (D-595).
 *
 * **`next` is honoured only where the rail says she may go.** `RequireSession`
 * writes `?next=` from wherever a signed-out visit was headed, and sign-in
 * used to go straight back there. QA found the case that breaks: a browser
 * bounced off an admin screen, then used to create a parent's account, sends
 * the brand-new parent to *Not your page* the moment she signs in. The
 * address was somebody else's, from a different session, and nothing checked.
 *
 * **One function answers "may she go here"** - `canReach`, the same one the
 * rail and the route guards call (D-322) - so this is not a second opinion
 * about permissions, only a second caller. A path the rail does not file at
 * all (a notification link, her account, a search hit) is passed through:
 * its own guard answers there, and refusing it here would break the one
 * journey `next` was written for, an emailed link opened while signed out.
 *
 * The root decides where a person belongs (D-176), so that is the fallback,
 * never `/calendar`.
 */
export function landingAfterSignIn(session: Session, next: string | null): string {
  // A protocol-relative `//host` is an open redirect dressed as a path, and
  // so is `/\host`: the URL parser treats a backslash as a slash, and so does
  // the router's own absolute-URL test.
  if (!next || !/^\/(?![/\\])/.test(next)) return '/'

  const pathname = next.split(/[?#]/)[0]
  const key = destinationForPath(pathname) ?? destinationByPrefix(pathname)

  if (key === null) return next

  return canReach(navSubjectFrom(session), key) ? next : '/'
}

